import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, LogIn, UserPlus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/logo.png";
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  // Professional button animations
  const buttonVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: { 
      scale: 0.98,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    }
  };

  // Sign Up button animation (original)
  const signupIconVariants = {
    animate: {
      y: [0, -3, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };

  // Navigation link animation - similar to Sign Up but different (subtle pulse + gradient)
  const navLinkVariants = {
    initial: { 
      y: 0,
      color: "#94a3b8"
    },
    hover: { 
      y: -2,
      color: "#22d3ee",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 20,
        duration: 0.2
      }
    }
  };

  const navGlowVariants = {
    initial: { 
      scale: 0.8,
      opacity: 0,
      background: "radial-gradient(circle, rgba(6,182,212,0) 0%, rgba(139,92,246,0) 100%)"
    },
    hover: { 
      scale: 1.2,
      opacity: 0.15,
      background: "radial-gradient(circle, rgba(6,182,212,0.8) 0%, rgba(139,92,246,0.8) 100%)",
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const navUnderlineVariants = {
    initial: { 
      width: "0%",
      opacity: 0,
      background: "linear-gradient(90deg, rgba(6,182,212,0) 0%, rgba(139,92,246,0) 100%)"
    },
    hover: { 
      width: "100%",
      opacity: 1,
      background: "linear-gradient(90deg, rgba(6,182,212,1) 0%, rgba(139,92,246,1) 100%)",
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    active: {
      width: "100%",
      opacity: 1,
      background: "linear-gradient(90deg, rgba(6,182,212,1) 0%, rgba(139,92,246,1) 100%)"
    }
  };

  const navIconVariants = {
    initial: { 
      opacity: 0,
      scale: 0,
      x: -5
    },
    hover: { 
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.25,
        ease: "backOut"
      }
    }
  };

  // Pulse animation for active links (similar to Sign Up but different)
  const activePulseVariants = {
    initial: { 
      scale: 1,
      boxShadow: "0 0 0 0 rgba(6,182,212,0)"
    },
    animate: {
      scale: [1, 1.02, 1],
      boxShadow: [
        "0 0 0 0 rgba(6,182,212,0)",
        "0 0 0 4px rgba(6,182,212,0.3)",
        "0 0 0 0 rgba(6,182,212,0)"
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut"
      }
    }
  };

  // Mobile menu animation
  const mobileMenuVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50 shadow-xl shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500 blur-xl opacity-50" />
                <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="font-black text-white text-lg">QB</span>
                </div>
              </div>
              <div>
                <h2 className="font-bold text-white text-lg">QBKart</h2>
                <p className="text-xs text-slate-400 -mt-1">IT Solutions</p>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation - Increased gap to gap-20 */}
          <nav className="hidden lg:flex items-center gap-20">
            {navLinks.map((link) => (
              <NavLink key={link.name} to={link.path}>
                {({ isActive }) => (
                  <motion.div 
                    className="relative"
                    variants={navLinkVariants}
                    initial="initial"
                    whileHover="hover"
                  >
                    {/* Similar to Sign Up - Pulse effect on active links */}
                    {isActive && (
                      <motion.div
                        className="absolute -inset-2 rounded-lg pointer-events-none"
                        variants={activePulseVariants}
                        initial="initial"
                        animate="animate"
                      />
                    )}

                    {/* Gradient glow effect (similar to Sign Up's gradient but softer) */}
                    <motion.div
                      className="absolute -inset-2 rounded-lg pointer-events-none"
                      variants={navGlowVariants}
                      initial="initial"
                      whileHover="hover"
                    />

                    {/* Icon that appears on hover (similar to Sign Up's icon animation) */}
                    <motion.div
                      className="absolute -left-5 top-1/2 -translate-y-1/2 text-cyan-400"
                      variants={navIconVariants}
                      initial="initial"
                      whileHover="hover"
                    >
                      <div className="w-1 h-1 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500" />
                    </motion.div>

                    <span
                      className={`text-sm font-medium transition-colors duration-200 relative z-10 ${
                        isActive ? "text-cyan-400" : "text-slate-300"
                      }`}
                    >
                      {link.name}
                    </span>

                    {/* Gradient underline (similar to Sign Up's gradient style) */}
                    <motion.div
                      className="absolute -bottom-2 left-0 h-[2px] rounded-full"
                      variants={navUnderlineVariants}
                      initial="initial"
                      animate={isActive ? "active" : "initial"}
                      whileHover="hover"
                    />
                  </motion.div>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <motion.div
              variants={buttonVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
            >
              <Link
                to="/login"
                className="px-5 py-2 rounded-xl border border-slate-700 text-slate-300 hover:text-cyan-400 hover:border-cyan-500 transition-all duration-200 flex items-center gap-2"
              >
                <LogIn size={18} />
                <span>Login</span>
              </Link>
            </motion.div>

            <motion.div
              variants={buttonVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
            >
              <Link
                to="/signup"
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-200 flex items-center gap-2 group"
              >
                <motion.div
                  variants={signupIconVariants}
                  animate="animate"
                >
                  <UserPlus size={18} />
                </motion.div>
                <span>Sign Up</span>
                <motion.div
                  className="absolute inset-0 bg-white/20 rounded-xl"
                  initial={{ scale: 0, opacity: 0 }}
                  whileHover={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white"
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-slate-800"
          >
            <div className="px-6 py-6 flex flex-col gap-6">
              {navLinks.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium py-2 transition-all duration-200 relative ${
                      isActive ? "text-cyan-400" : "text-slate-300"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="relative z-10">{link.name}</span>
                      {isActive && (
                        <motion.div
                          className="absolute inset-0 -inset-x-4 rounded-lg bg-gradient-to-r from-cyan-500/10 to-purple-500/10"
                          layoutId="mobileActiveBg"
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
              <div className="border-t border-slate-800 pt-6 space-y-4">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:text-cyan-400 hover:border-cyan-500 transition-all duration-200"
                >
                  <LogIn size={18} />
                  <span>Login</span>
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium shadow-lg shadow-cyan-500/25 relative overflow-hidden group"
                >
                  <motion.div
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
                  >
                    <UserPlus size={18} />
                  </motion.div>
                  <span>Sign Up</span>
                  <motion.div
                    className="absolute inset-0 bg-white/20"
                    initial={{ scale: 0, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;